import React, { memo, useCallback, useEffect, useMemo } from 'react';
import cx from 'clsx';

import { useSwitch } from 'hooks';

import { RowProps, TableProps } from './types';
import { Cells } from './Cell';
import './index.css';

type RowPropsAll<T extends Record<string, any>> = RowProps<T> &
  Pick<
    TableProps<T>,
    | 'columns'
    | 'rowRender'
    | 'subRowColumns'
    | 'expandedRowRender'
    | 'expandedIconPosition'
    | 'onRowClick'
    | 'skeleton'
  > & {
    onExpandChange?(collapsed: boolean, row: T, index: number): void;
  };

export const Row = memo(
  <T extends Record<string, any>>({
    columns,
    row,
    rowRender,
    index,
    subRowColumns,
    expandedRowRender,
    onExpandChange,
    expanded,
    expandedIconPosition,
    onRowClick,
    skeleton,
  }: RowPropsAll<T>) => {
    const isRowExpand = useMemo(() => expandedIconPosition === 'right', [expandedIconPosition]);

    const { on, turn } = useSwitch(expanded);

    useEffect(() => {
      turn(expanded!);
    }, [expanded, turn]);

    const handleExpandChange = useCallback(() => {
      onExpandChange?.(!on, row, index);
    }, [onExpandChange, on, row, index]);

    const handleRowClick = useCallback(() => {
      if (onRowClick) {
        onRowClick?.(row, index);
      }

      if (isRowExpand) {
        handleExpandChange();
      }
    }, [handleExpandChange, row, index, isRowExpand, onRowClick]);

    const cells = (
      <Cells
        columns={columns}
        row={row}
        subRowColumns={subRowColumns}
        rowIndex={index}
        expandedRowRender={expandedRowRender}
        onExpandChange={handleExpandChange}
        expanded={on}
        expandedIconPosition={expandedIconPosition}
        skeleton={skeleton}
      />
    );

    return rowRender ? (
      React.createElement(rowRender, {
        row,
        index,
        children: cells,
        expanded: on,
        onExpandChange: handleExpandChange,
        onRowClick: handleRowClick,
      })
    ) : (
      <div
        onClick={handleRowClick}
        className={cx(
          'db-table-wrappedRow',
          on && 'is-expanded',
          (isRowExpand || onRowClick) && 'is-clickable',
        )}>
        {cells}
      </div>
    );
  },
);
